import { useState } from "react";
import {
  type BroadcastAudience,
  type BroadcastTemplate,
  createAdminBroadcast,
} from "../../api/admin-communications";
import { t } from "../../i18n";
import { styles } from "./communications-compose-styles";
import {
  BROADCAST_TEMPLATES,
  templateLabel,
} from "./communications-formatters";

type SubmitState = {
  readonly error: string | null;
  readonly message: string | null;
  readonly submitting: boolean;
};

type AudienceMode = "all" | "affected";

type ComposeFormValues = {
  readonly template: BroadcastTemplate;
  readonly reason: string;
  readonly paramsText: string;
  readonly audienceMode: AudienceMode;
  readonly affectedUsers: string;
};

type ComposeFormState = {
  readonly values: ComposeFormValues;
  readonly submitState: SubmitState;
  readonly update: (patch: Partial<ComposeFormValues>) => void;
  readonly submit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

const DEFAULT_PARAMS = '{\n  "affected_features": "sync"\n}';

const INITIAL_VALUES: ComposeFormValues = {
  template: "service_degraded_warning",
  reason: "",
  paramsText: DEFAULT_PARAMS,
  audienceMode: "all",
  affectedUsers: "",
};

const INITIAL_SUBMIT_STATE: SubmitState = {
  error: null,
  message: null,
  submitting: false,
};

export function CommunicationsCompose({
  onCreated,
}: {
  onCreated: () => void;
}): React.JSX.Element {
  const form = useComposeForm(onCreated);

  return (
    <form style={styles.form} onSubmit={(event) => void form.submit(event)}>
      <TemplateField form={form} />
      <TextAreaField
        field="reason"
        label={t("communications.reason")}
        form={form}
      />
      <TextAreaField
        field="paramsText"
        label={t("communications.params_json")}
        form={form}
        mono
        rows={6}
      />
      <AudienceFields form={form} />
      <SubmitFeedback state={form.submitState} />
      <button
        type="submit"
        disabled={form.submitState.submitting}
        style={styles.button}
      >
        {form.submitState.submitting
          ? t("communications.submitting")
          : t("communications.submit")}
      </button>
    </form>
  );
}

function useComposeForm(onCreated: () => void): ComposeFormState {
  const [values, setValues] = useState<ComposeFormValues>(INITIAL_VALUES);
  const [submitState, setSubmitState] =
    useState<SubmitState>(INITIAL_SUBMIT_STATE);

  function update(patch: Partial<ComposeFormValues>): void {
    setValues((current) => ({ ...current, ...patch }));
  }

  async function submit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    setSubmitState({ error: null, message: null, submitting: true });
    try {
      const result = await createAdminBroadcast({
        template: values.template,
        reason: values.reason,
        params: parseParams(values.paramsText),
        audience: parseAudience(values.audienceMode, values.affectedUsers),
      });
      setSubmitState({
        error: null,
        message: t("communications.created", { id: result.broadcast_id }),
        submitting: false,
      });
      onCreated();
    } catch (error) {
      setSubmitState({
        error:
          error instanceof Error ? error.message : t("common.action_failed"),
        message: null,
        submitting: false,
      });
    }
  }

  return { values, submitState, update, submit };
}

function TemplateField({
  form,
}: { form: ComposeFormState }): React.JSX.Element {
  return (
    <FormRow
      label={t("communications.template")}
      htmlFor="communications-template"
    >
      <select
        id="communications-template"
        value={form.values.template}
        onChange={(event) =>
          form.update({ template: event.target.value as BroadcastTemplate })
        }
        style={styles.input}
      >
        {BROADCAST_TEMPLATES.map((item) => (
          <option key={item} value={item}>
            {templateLabel(item)}
          </option>
        ))}
      </select>
    </FormRow>
  );
}

function TextAreaField({
  field,
  label,
  form,
  mono = false,
  rows = 3,
}: {
  field: "reason" | "paramsText";
  label: string;
  form: ComposeFormState;
  mono?: boolean;
  rows?: number;
}): React.JSX.Element {
  const id = `communications-${field}`;
  return (
    <FormRow label={label} htmlFor={id}>
      <textarea
        id={id}
        value={form.values[field]}
        onChange={(event) => updateTextField(form, field, event.target.value)}
        rows={rows}
        style={mono ? styles.textareaMono : styles.textarea}
      />
    </FormRow>
  );
}

function updateTextField(
  form: ComposeFormState,
  field: "reason" | "paramsText",
  value: string,
): void {
  if (field === "reason") {
    form.update({ reason: value });
    return;
  }
  form.update({ paramsText: value });
}

function AudienceFields({
  form,
}: {
  form: ComposeFormState;
}): React.JSX.Element {
  return (
    <>
      <FormRow
        label={t("communications.audience")}
        htmlFor="communications-audience"
      >
        <select
          id="communications-audience"
          value={form.values.audienceMode}
          onChange={(event) =>
            form.update({ audienceMode: parseAudienceMode(event.target.value) })
          }
          style={styles.input}
        >
          <option value="all">{t("communications.audience_all")}</option>
          <option value="affected">
            {t("communications.audience_affected")}
          </option>
        </select>
      </FormRow>
      {form.values.audienceMode === "affected" && (
        <FormRow
          label={t("communications.affected_user_ids")}
          htmlFor="communications-affected-users"
        >
          <textarea
            id="communications-affected-users"
            value={form.values.affectedUsers}
            onChange={(event) =>
              form.update({ affectedUsers: event.target.value })
            }
            rows={4}
            style={styles.textareaMono}
          />
        </FormRow>
      )}
    </>
  );
}

function SubmitFeedback({ state }: { state: SubmitState }): React.JSX.Element {
  return (
    <>
      {state.error && <p style={styles.error}>{state.error}</p>}
      {state.message && <p style={styles.success}>{state.message}</p>}
    </>
  );
}

function parseAudienceMode(value: string): AudienceMode {
  return value === "affected" ? "affected" : "all";
}

function parseAudience(
  mode: AudienceMode,
  rawUsers: string,
): BroadcastAudience {
  if (mode === "all") return { type: "all" };
  const userIds = rawUsers
    .split(/[\s,]+/u)
    .map((value) => value.trim())
    .filter(Boolean);
  return { type: "affected_user_ids", user_ids: userIds };
}

function parseParams(raw: string): Record<string, string> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    throw new Error(t("communications.params_invalid"));
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(t("communications.params_invalid"));
  }
  return Object.fromEntries(
    Object.entries(parsed).map(([key, value]) => [key, String(value)]),
  );
}

function FormRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={styles.row}>
      <label htmlFor={htmlFor} style={styles.label}>
        {label}
      </label>
      {children}
    </div>
  );
}
